import { ReactNode, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Button } from 'components/atoms/button'
import { CodeInput } from 'components/atoms/code-input'
import { useCountdown } from 'hooks/use-countdown'
import { EMAIL_CODE_LENGTH } from 'utils/email-code-sign-in'

interface EmailCodeFormProps {
	email: string
	nextResend: string | null
	blockedUntil: string | null
	/** The last code was refused as expired / already used: only a resend helps */
	codeExpired: boolean
	sending: boolean
	verifying: boolean
	/** Bumped to clear the code after a refused attempt */
	resetKey: number
	/** Resend calls email/start, which needs a Turnstile token */
	resendReady: boolean
	/** Rendered under the actions (the Turnstile widget for resend) */
	children?: ReactNode
	onVerify: (code: string) => void
	onResend: () => void
	onChangeEmail: () => void
	onContinueWithPassword: () => void
}

const CODE_INPUT_ID = 'email-sign-in-code'

export function EmailCodeForm({
	email,
	nextResend,
	blockedUntil,
	codeExpired,
	sending,
	verifying,
	resetKey,
	resendReady,
	children,
	onVerify,
	onResend,
	onChangeEmail,
	onContinueWithPassword,
}: Readonly<EmailCodeFormProps>) {
	const [code, setCode] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)
	const passwordButtonRef = useRef<HTMLButtonElement>(null)
	const resend = useCountdown(nextResend)
	const block = useCountdown(blockedUntil)
	const isBlocked = block.secondsLeft > 0
	const busy = sending || verifying
	// Not blocked on codeExpired: a newer code from an earlier resend may already be in the inbox
	const canVerify = code.length === EMAIL_CODE_LENGTH && !busy && !isBlocked

	// Focus on arrival, and again once a refused code is cleared
	useEffect(() => {
		setCode('')
		inputRef.current?.focus()
	}, [resetKey])

	// The code field disables on lockout, which would drop keyboard focus to the page: hand it to the way forward
	useEffect(() => {
		if (isBlocked) passwordButtonRef.current?.focus()
	}, [isBlocked])

	const handleCodeChange = (value: string) => {
		setCode(value)
		if (value.length === EMAIL_CODE_LENGTH && !busy && !isBlocked) onVerify(value)
	}

	const resendLabel = () => {
		if (sending) return 'Sending…'
		if (resend.secondsLeft > 0) return `Resend code in ${resend.formatted}`
		return 'Resend code'
	}

	return (
		<div className="space-y-6">
			<p className="text-center text-secondary-text">
				We sent a 6-digit code to <span className="break-words font-semibold text-primary-text">{email}</span>.
				It expires in 10 minutes.
			</p>
			<form
				noValidate
				onSubmit={event => {
					event.preventDefault()
					if (canVerify) onVerify(code)
				}}
				className="space-y-4"
			>
				<div className="space-y-2">
					<label htmlFor={CODE_INPUT_ID} className="block text-sm font-medium text-primary-text">
						Enter your 6-digit code
					</label>
					<CodeInput
						ref={inputRef}
						id={CODE_INPUT_ID}
						value={code}
						onChange={handleCodeChange}
						length={EMAIL_CODE_LENGTH}
						// Read-only while verifying keeps focus (and the mobile keyboard); disabled only for the lockout
						readOnly={verifying}
						disabled={isBlocked}
						aria-describedby={isBlocked ? `${CODE_INPUT_ID}-blocked` : undefined}
					/>
					{isBlocked && (
						<div className="space-y-1 text-sm">
							{/* The announced notice is static: the ticking countdown lives outside it, or a screen reader would
							    repeat the whole message every second */}
							<output id={`${CODE_INPUT_ID}-blocked`} className="block text-secondary-red">
								Code sign-in is paused for this email. Continue with your password, or try again later.
							</output>
							<p className="text-secondary-text">Try again in {block.formatted}</p>
						</div>
					)}
				</div>
				<Button
					variant="primary"
					fullWidth
					size="lg"
					type="submit"
					className="flex items-center justify-center gap-1"
					disabled={!canVerify}
				>
					{verifying && <FontAwesomeIcon icon={['fas', 'spinner']} spin />}
					Verify code
				</Button>
			</form>
			<div className="flex flex-col items-center gap-3">
				<Button
					variant={codeExpired ? 'primary' : 'link'}
					fullWidth={codeExpired}
					size={codeExpired ? 'lg' : 'md'}
					// Plain text link: no pill from the disabled blur and rounding while the cooldown runs. -my-2.5 py-2.5
					// makes a 44px touch target without moving the layout.
					className={
						codeExpired ? undefined : '!px-0 !py-2.5 -my-2.5 !rounded-none disabled:!backdrop-blur-none'
					}
					onClick={onResend}
					disabled={busy || isBlocked || resend.secondsLeft > 0 || !resendReady}
				>
					{resendLabel()}
				</Button>
				<Button
					ref={passwordButtonRef}
					variant="outline"
					fullWidth
					size="lg"
					onClick={onContinueWithPassword}
					disabled={busy}
				>
					Continue with password
				</Button>
				<Button variant="link" className="-my-2.5 py-2.5" onClick={onChangeEmail} disabled={busy}>
					Change email
				</Button>
				<p className="text-center text-xs text-secondary-text">
					Can't find it? Check your spam or promotions folder.
				</p>
			</div>
			{children}
		</div>
	)
}

export type { EmailCodeFormProps }
